import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatINR, productImg } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Package, Sparkles } from "lucide-react";

const STATUS = {
  processing: { bg: "var(--rose)",      fg: "var(--ink)" },
  shipped:    { bg: "#E7DFFB",          fg: "#3F2C6E" },
  delivered:  { bg: "#D6F1DD",          fg: "#1F5934" },
  cancelled:  { bg: "#E5E0E2",          fg: "var(--ink-soft)" },
};

export default function Account() {
  const { user, logout } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    api.get("/orders/mine").then(({ data }) => { setOrders(data); setLoading(false); }).catch(() => setLoading(false));
  }, [user]);

  if (!user) {
    return (
      <div data-testid="account-guest" className="min-h-screen flex items-center justify-center px-6">
        <div className="surface p-10 text-center max-w-md">
          <span className="eyebrow">Sign in</span>
          <h2 className="text-[2rem] mt-3 mb-3">See your orders</h2>
          <p className="text-[var(--ink-soft)] mb-5">Log in to view your order history.</p>
          <Link to="/login" data-testid="account-login-cta" className="btn btn-primary">Sign in</Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="account-page" className="min-h-screen pb-20">
      <div className="max-w-[1180px] mx-auto px-6 pt-12">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <span className="eyebrow">Your account</span>
            <h1 className="text-[clamp(2.4rem,5vw,3.5rem)] mt-3">Hi, {user.name?.split(" ")[0] || "gloss"} ♡</h1>
          </div>
          <button onClick={logout} data-testid="account-logout" className="text-sm font-bold uppercase tracking-[0.16em]" style={{color:"var(--bloom)"}}>
            Sign out
          </button>
        </div>

        <div className="mt-10 surface p-8">
          <div className="flex items-center gap-2 mb-5">
            <Package className="w-5 h-5" style={{color:"var(--bloom)"}}/>
            <h2 className="font-serif text-[1.6rem] font-semibold">Order history</h2>
          </div>
          {loading ? (
            <div className="text-[var(--ink-soft)]">Loading…</div>
          ) : orders.length === 0 ? (
            <div className="py-10 text-center">
              <Sparkles className="w-10 h-10 mx-auto" style={{color:"var(--bloom)"}}/>
              <div className="mt-3 font-serif text-[1.4rem] font-semibold">No orders yet</div>
              <Link to="/shop" className="btn btn-primary mt-5 inline-flex">Start shopping</Link>
            </div>
          ) : (
            <div className="space-y-4">
              {orders.map((o) => {
                const s = STATUS[o.fulfillment_status] || STATUS.processing;
                return (
                  <div key={o.id} data-testid={`order-${o.id}`} className="bg-white rounded-2xl border border-[#fde0e8] p-5">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <div className="font-serif font-semibold">#{o.id.slice(-8)}</div>
                        <div className="text-xs text-[var(--ink-soft)]">{new Date(o.created_at).toLocaleString()}</div>
                      </div>
                      <div className="flex gap-2">
                        <span className="text-[10px] uppercase font-extrabold px-3 py-1 rounded-full tracking-wider"
                          style={{ background: s.bg, color: s.fg }}>
                          {o.fulfillment_status}
                        </span>
                        <span className="text-[10px] uppercase font-extrabold px-3 py-1 rounded-full tracking-wider"
                          style={{ background: o.payment_status === "paid" ? "#D6F1DD" : "#FFEAC2", color: o.payment_status === "paid" ? "#1F5934" : "#7A5400" }}>
                          {o.payment_status}
                        </span>
                      </div>
                    </div>
                    <div className="mt-4 flex gap-3 overflow-x-auto">
                      {o.items.map((i) => (
                        <div key={i.product_id} className="flex-shrink-0 flex items-center gap-2 bg-[var(--petal)] rounded-xl p-2 pr-3">
                          <img src={productImg(i.image)} alt="" className="w-10 h-10 rounded-lg object-cover"/>
                          <div className="text-xs">
                            <div className="font-serif font-semibold">{i.name}</div>
                            <div className="text-[var(--ink-soft)]">× {i.qty}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="mt-4 flex justify-between font-bold">
                      <span className="text-[var(--ink-soft)]">Total</span>
                      <span className="font-serif" style={{color:"var(--ink)"}}>{formatINR(o.total)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
